import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import Navigation from '../components/Navigation';
import {
  Mail,
  Lock,
  User,
  Building,
  Briefcase,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

export default function Signup() {
  const { supabase } = useAuth();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    company: '',
    role: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const theme = localStorage.getItem('theme') || 'dark';
    document.documentElement.setAttribute('data-theme', theme);
  }, []);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const email = formData.email.trim().toLowerCase();
    const fullName = formData.fullName.trim();
    const company = formData.company.trim();
    const role = formData.role.trim();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password: formData.password,
        options: {
          data: {
            full_name: fullName,
            company: company || null,
            role: role || null
          }
        }
      });

      if (signUpError) {
        if (signUpError.message?.toLowerCase().includes('already registered')) {
          setError('An account with this email already exists');
        } else {
          setError(signUpError.message || 'Signup failed');
        }
        return;
      }

      if (data?.user) {
        setSuccess(true);
        return;
      }

      setError('Signup failed. Please try again.');
    } catch (err) {
      console.error('Signup error:', err);
      setError(err?.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="signup-page">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Roboto+Slab:wght@900&family=DM+Sans:wght@400;500;600&display=swap');

          :root[data-theme="dark"] {
            --bg-primary: var(--bg-app);
            --text-primary: #F4F1E8;
            --accent-green: #5FD4A0;
          }

          :root[data-theme="light"] {
            --bg-primary: #F8F6F1;
            --text-primary: #1A1612;
            --accent-green: #3BA876;
          }

          .signup-page {
            min-height: 100vh;
            background: var(--bg-primary);
            color: var(--text-primary);
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'DM Sans', sans-serif;
          }

          .success-container {
            text-align: center;
            padding: 2rem;
          }

          .success-icon {
            margin: 0 auto 1.5rem;
          }

          .success-title {
            font-size: 1.75rem;
            font-weight: 700;
            margin-bottom: 0.75rem;
          }

          .success-text {
            color: var(--text-primary);
            opacity: 0.7;
            margin-bottom: 2rem;
          }

          .btn-primary {
            display: inline-block;
            padding: 0.875rem 1.75rem;
            background: #D97642;
            color: white;
            border-radius: 0.625rem;
            text-decoration: none;
            font-weight: 600;
          }
        `}</style>

        <div className="success-container">
          <CheckCircle size={64} color="var(--accent-green)" className="success-icon" />
          <h2 className="success-title">Check Your Email</h2>
          <p className="success-text">Confirmation sent to {formData.email}</p>
          <Link to="/login" className="btn-primary">Go to Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto+Slab:wght@900&family=DM+Sans:wght@400;500;600&display=swap');

        :root[data-theme="dark"] {
          --bg-primary: var(--bg-app);
          --bg-secondary: #141918;
          --bg-tertiary: #1F2624;
          --text-primary: #F4F1E8;
          --text-secondary: #C2BDB0;
          --accent-primary: #D97642;
          --accent-red: #EF6B6B;
          --border-primary: rgba(212, 196, 168, 0.15);
          --border-subtle: rgba(212, 196, 168, 0.1);
        }

        :root[data-theme="light"] {
          --bg-primary: #F8F6F1;
          --bg-secondary: #FFFFFF;
          --bg-tertiary: #E8E4DB;
          --text-primary: #1A1612;
          --text-secondary: #4A453C;
          --accent-primary: #D97642;
          --accent-red: #D94F4F;
          --border-primary: rgba(26, 22, 18, 0.15);
          --border-subtle: rgba(26, 22, 18, 0.1);
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .signup-page {
          min-height: 100vh;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-family: 'DM Sans', sans-serif;
          display: flex;
          flex-direction: column;
        }

        .signup-content {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem 1rem;
        }

        .signup-container {
          width: 100%;
          max-width: 450px;
        }

        .signup-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .signup-logo {
          font-family: 'Roboto Slab', serif;
          font-weight: 900;
          font-size: 2.5rem;
          margin-bottom: 0.5rem;
        }

        .signup-subtitle {
          color: var(--text-secondary);
        }

        .signup-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-primary);
          border-radius: 1.25rem;
          padding: 2.5rem;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
        }

        .error-message {
          padding: 1rem;
          background: rgba(239, 107, 107, 0.15);
          border: 1px solid var(--accent-red);
          border-radius: 0.75rem;
          color: var(--accent-red);
          margin-bottom: 1.5rem;
          display: flex;
          align-items: start;
          gap: 0.75rem;
        }

        .form-group {
          margin-bottom: 1.25rem;
        }

        .form-label {
          display: block;
          font-weight: 500;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
        }

        .input-wrapper {
          position: relative;
        }

        .input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-secondary);
        }

        .form-input {
          width: 100%;
          padding: 0.875rem 1rem 0.875rem 3rem;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-primary);
          border-radius: 0.625rem;
          color: var(--text-primary);
          font-family: inherit;
          font-size: 1rem;
          transition: border-color 0.2s;
        }

        .form-input:focus {
          outline: none;
          border-color: var(--accent-primary);
        }

        .btn-primary {
          width: 100%;
          padding: 0.875rem;
          background: var(--accent-primary);
          border: none;
          border-radius: 0.625rem;
          color: white;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s;
          font-family: inherit;
        }

        .btn-primary:hover {
          background: #C56838;
          transform: translateY(-1px);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .login-link {
          margin-top: 1.5rem;
          text-align: center;
          color: var(--text-secondary);
        }

        .login-link a {
          color: var(--accent-primary);
          font-weight: 600;
          text-decoration: none;
        }

        .login-link a:hover {
          text-decoration: underline;
        }

        @media (max-width: 768px) {
          .signup-card {
            padding: 2rem;
          }
        }
      `}</style>

      <Navigation />

      <div className="signup-content">
        <div className="signup-container">
          <div className="signup-header">
            <h1 className="signup-logo">MIZAR</h1>
            <p className="signup-subtitle">Create your account</p>
          </div>

          <div className="signup-card">
            {error && (
              <div className="error-message">
                <AlertCircle size={20} style={{ flexShrink: 0 }} />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <div className="input-wrapper">
                  <User size={20} className="input-icon" />
                  <input
                    type="text"
                    className="form-input"
                    value={formData.fullName}
                    onChange={handleChange('fullName')}
                    placeholder="John Smith"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <div className="input-wrapper">
                  <Mail size={20} className="input-icon" />
                  <input
                    type="email"
                    className="form-input"
                    value={formData.email}
                    onChange={handleChange('email')}
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <div className="input-wrapper">
                  <Lock size={20} className="input-icon" />
                  <input
                    type="password"
                    className="form-input"
                    value={formData.password}
                    onChange={handleChange('password')}
                    placeholder="At least 8 characters"
                    minLength={8}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Company (optional)</label>
                <div className="input-wrapper">
                  <Building size={20} className="input-icon" />
                  <input
                    type="text"
                    className="form-input"
                    value={formData.company}
                    onChange={handleChange('company')}
                    placeholder="Your company"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Role (optional)</label>
                <div className="input-wrapper">
                  <Briefcase size={20} className="input-icon" />
                  <input
                    type="text"
                    className="form-input"
                    value={formData.role}
                    onChange={handleChange('role')}
                    placeholder="Product Manager"
                  />
                </div>
              </div>

              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>

            <p className="login-link">
              Already have an account? <Link to="/login">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
